import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { apiFetch } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";

interface ConnectorInfo {
  mcpUrl: string;
  scopes: string[];
  docsUrl: string;
}

interface AuthorizationListing {
  clientId: string;
  clientName: string;
  scope: string;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string;
  activeTokenCount: number;
}

interface AuthorizationsResponse {
  authorizations: AuthorizationListing[];
}

function formatTimestamp(value: string | null): string {
  if (!value) return "never";
  const ts = new Date(value);
  if (Number.isNaN(ts.getTime())) return value;
  return ts.toLocaleString();
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      onClick={() => {
        void navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        });
      }}
      aria-label="Copy connector URL"
    >
      {copied ? (
        <>
          <Check className="h-4 w-4" aria-hidden /> Copied
        </>
      ) : (
        <>
          <Copy className="h-4 w-4" aria-hidden /> Copy
        </>
      )}
    </Button>
  );
}

const SCOPE_DESCRIPTIONS: Record<string, string> = {
  "bom:read": "Read BoM templates, revisions, parse rules, and run preview functions.",
  "bom:write_drafts": "Edit draft (unsubmitted, unapproved) BoM revisions only.",
  "manufacturing:read": "Read manufacturing work orders, calendar, daily scans, alternates, batches, device lookup.",
  "manufacturing:write": "Manufacturing write tools (work orders, rebuild proposal queue).",
  "manufacturing_management:read": "CM purchase orders, transfers, potting send drafts, forward demand.",
  "documents:read": "Controlled documents, collections, change orders, drawing gaps.",
  "shipping:read": "Shipments, GOSI details, inbound list, carriers.",
  "wiki:read": "Outline wiki search and documents (Outline sign-in required).",
  "clampqc:read": "Clamp QC work orders and failure reason codes.",
  "accounting:read": "Cash flow, EOM months, AP invoice headers.",
  "mrp:read": "MRP components, inventory, AML, purchase orders, planning.",
};

export function McpAuthorizationsPage() {
  const queryClient = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("erp");
  const dataConnectorsReadOnlyTitle = "Your role has read-only access to the Data connectors hub module.";

  const connectorQuery = useQuery({
    queryKey: ["mcp", "connector-info"],
    queryFn: () => apiFetch<ConnectorInfo>("/api/mcp/integrations/connector-info"),
  });

  const authsQuery = useQuery({
    queryKey: ["mcp", "authorizations"],
    queryFn: () =>
      apiFetch<AuthorizationsResponse>("/api/mcp/integrations/me/authorizations"),
  });

  const revokeMutation = useMutation({
    mutationFn: (clientId: string) =>
      apiFetch<{ revoked: number }>(
        `/api/mcp/integrations/me/authorizations/${encodeURIComponent(clientId)}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["mcp", "authorizations"] });
    },
  });

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageTitleWithHelp
        titleClassName="text-2xl"
        hoverHint="Connect ChatGPT or Claude to /api/mcp (short menu: playbook, hub_search, hub_get, hub_propose, BoM tools). Draft writes only via hub_propose and BoM drafts; operational manufacturing writes are not on MCP. Asana tasks use Asana's connector."
      >
        Hub MCP connector
      </PageTitleWithHelp>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Connector URL</CardTitle>
          </CardHeader>
          <CardBody>
            {connectorQuery.isLoading ? (
              <p className="text-sm text-slate-500">Loading connector details&hellip;</p>
            ) : connectorQuery.error ? (
              <p className="text-sm text-rose-700">
                Could not load connector info: {(connectorQuery.error as Error).message}
              </p>
            ) : connectorQuery.data ? (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <code className="rounded bg-slate-100 px-3 py-2 font-mono text-sm text-slate-900">
                    {connectorQuery.data.mcpUrl}
                  </code>
                  <CopyButton value={connectorQuery.data.mcpUrl} />
                </div>
                <div className="text-sm text-slate-600">
                  <p className="font-medium text-slate-900">How to add it in ChatGPT</p>
                  <ol className="mt-2 list-decimal space-y-1 pl-5">
                    <li>Open ChatGPT, then Settings, then Connectors (or Plugins with Developer Mode).</li>
                    <li>Choose Add custom connector or Server URL.</li>
                    <li>Paste the URL above and follow the consent prompt that opens in your browser.</li>
                    <li>Approve the requested scopes. Add Asana's connector separately for task actions.</li>
                  </ol>
                </div>
                <div className="text-sm text-slate-600">
                  <p className="font-medium text-slate-900">How to add it in Claude</p>
                  <ol className="mt-2 list-decimal space-y-1 pl-5">
                    <li>Open Claude, then Settings, then Connectors.</li>
                    <li>Add a custom connector and paste the URL above.</li>
                    <li>Sign in on the hub consent page that opens in your browser.</li>
                    <li>
                      Add Asana's connector (
                      <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-xs">
                        https://mcp.asana.com/v2/mcp
                      </code>
                      ) in the same Claude settings so the assistant can update tasks.
                    </li>
                  </ol>
                </div>
                <div className="text-sm text-slate-600">
                  <p className="font-medium text-slate-900">Available scopes</p>
                  <ul className="mt-2 space-y-1">
                    {connectorQuery.data.scopes.map((scope) => (
                      <li key={scope}>
                        <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs">
                          {scope}
                        </code>{" "}
                        {SCOPE_DESCRIPTIONS[scope] ?? "Hub module scope."}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : null}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Active authorizations</CardTitle>
          </CardHeader>
          <CardBody>
            {authsQuery.isLoading ? (
              <p className="text-sm text-slate-500">Loading authorizations&hellip;</p>
            ) : authsQuery.error ? (
              <p className="text-sm text-rose-700">
                Could not load authorizations: {(authsQuery.error as Error).message}
              </p>
            ) : authsQuery.data?.authorizations.length ? (
              <ul className="divide-y divide-slate-200">
                {authsQuery.data.authorizations.map((auth) => (
                  <li
                    key={auth.clientId}
                    className="flex flex-wrap items-start justify-between gap-3 py-4"
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900">{auth.clientName}</p>
                      <p className="mt-0.5 font-mono text-xs text-slate-500">{auth.clientId}</p>
                      <p className="mt-1 text-xs text-slate-600">
                        Scopes:{" "}
                        {auth.scope
                          .split(/\s+/)
                          .filter((s) => s.length > 0)
                          .map((scope) => (
                            <code
                              key={scope}
                              className="mr-1 rounded bg-slate-100 px-1 py-0.5 font-mono text-xs"
                            >
                              {scope}
                            </code>
                          ))}
                      </p>
                      <p className="mt-1 text-xs text-slate-600">
                        First authorized {formatTimestamp(auth.createdAt)} &middot; Last used{" "}
                        {formatTimestamp(auth.lastUsedAt)} &middot; Token expires{" "}
                        {formatTimestamp(auth.expiresAt)}
                      </p>
                    </div>
                    <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={dataConnectorsReadOnlyTitle}>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      disabled={!hubWritesAllowed}
                      onClick={() => revokeMutation.mutate(auth.clientId)}
                      isLoading={
                        revokeMutation.isPending && revokeMutation.variables === auth.clientId
                      }
                    >
                      Revoke
                    </Button>
                    </ReadOnlyHoverLayer>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500">
                No active authorizations. Add the connector in ChatGPT or Claude and approve it to see it
                here.
              </p>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
